import { microServicesRoutes } from '@constants';
import {
  type ApiResponse, type SalesforceAccount,
  type SalesforceAppConfig
} from '@types';
import { syncApi } from '@lib/apiClients.ts';

export default function useSalesforceApi (): {
  getSalesforceConfigAsync: (
    userId: string,
    orgId: string
  ) => Promise<ApiResponse<SalesforceAppConfig>>;
  connectSalesforceLogin: (
    userId: string,
    orgId: string,
    sandboxIsChecked: boolean,
  ) => Promise<ApiResponse<{ url: string }>>;
  saveSalesforceConfig: (
    salesforceConfig: SalesforceAppConfig,
    orgId: string,
    userId: string,
  ) => Promise<ApiResponse<SalesforceAppConfig>>;
  getConnectedAccounts: (
    userId: string,
    orgId: string
  ) => Promise<ApiResponse<SalesforceAccount[]>>;
  deleteAccountAsync: (
    orgId: string,
    userId: string,
    nonce: string
  ) => Promise<ApiResponse<SalesforceAccount>>
  } {

  const connectSalesforceLogin = async (
    userId: string,
    orgId: string,
    sandboxIsChecked: boolean,
  ): Promise<ApiResponse<{ url: string }>> => {
    try {
      const res = await syncApi.post<{ url: string }>(
        `${microServicesRoutes.sync.get.salesforce.oAuthLogin}/${orgId}/${userId}`.concat(
          `?sandbox=${sandboxIsChecked}`)
      );

      if (res.status === 201) {
        window.open(res.data.url, 'salesforcePopup', `width=${500},height=${500}`);
      }

      return {
        success: true,
        data: res.data,
      };
    } catch (err) {
      return  {
        error: { message:
              'Error trying to get login salesforce' 
        }, success: false };
    }
  };

  const getSalesforceConfigAsync = async (
    userId: string,
    orgId: string
  ): Promise<ApiResponse<SalesforceAppConfig>> => {
    try {
      const res = await syncApi.get<SalesforceAppConfig>(
        `${microServicesRoutes.sync.get.salesforce.getSalesforceConfiguration}/${orgId}/${userId}`,
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (e) {
      return  {
        error: { message:
          'Error trying to Salesforce configs' 
        }, success: false };
    }
  };

  const saveSalesforceConfig = async (
    salesforceConfig: SalesforceAppConfig,
    orgId: string,
    userId: string,
  ):Promise<ApiResponse<SalesforceAppConfig>> => {
    try {
      const res = await syncApi.post<SalesforceAppConfig>(
        `${microServicesRoutes.sync.post.salesforce.saveSalesforceConfiguration}/${orgId}/${userId}`,
        salesforceConfig,
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (e) {
      return  {
        error: { message:
              'Error trying to save config' 
        }, success: false };
    }
  };

  const getConnectedAccounts = async (
    userId: string,
    orgId: string
  ): Promise<ApiResponse<SalesforceAccount[]>> => {
    try {
      const res = await syncApi.get<SalesforceAccount[]>(
        `${microServicesRoutes.sync.get.salesforce.getLinkedAccounts}/${orgId}/${userId}`,
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (e) {
      return { 
        error: 
          { message: 
                'Error while getting salesforce accounts.' 
          }, success: false 
      };
    }
  };

  const deleteAccountAsync = async (
    orgId: string,
    userId: string,
    nonce: string
  ):Promise<ApiResponse<SalesforceAccount>> => {
    try {
      const res = await syncApi.delete<SalesforceAccount>(
        `${microServicesRoutes.sync.delete.salesforce.deleteSalesforceAccount}/${orgId}/${userId}/${nonce}`,
      );

      return {
        success: true,
        data: res.data,
      };
    } catch (e) {
      return { error: { message: 
          'Error to delete Account'
      }, success: false };
    }
  };

  return {
    getSalesforceConfigAsync,
    connectSalesforceLogin,
    saveSalesforceConfig,
    getConnectedAccounts,
    deleteAccountAsync,
  };
}
